/**
 * Global confirm() function backed by a portal-rendered AlertDialog.
 * Import `<ConfirmDialogHost />` once in an app-level component, then call
 * `confirm({ title, message, confirmLabel, destructive })` from anywhere.
 *
 * Returns a Promise<boolean> that resolves to `true` if the user confirms
 * and `false` if they cancel.
 */
import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

let requestId = 0;
const listeners = new Set();

export function confirm({
  title = "Weet je het zeker?",
  message = "",
  confirmLabel = "Bevestigen",
  cancelLabel = "Annuleren",
  destructive = false,
} = {}) {
  return new Promise((resolve) => {
    const id = ++requestId;
    const payload = { id, title, message, confirmLabel, cancelLabel, destructive, resolve };
    listeners.forEach((l) => l(payload));
  });
}

export function ConfirmDialogHost() {
  const [request, setRequest] = useState(null);

  useEffect(() => {
    const handler = (payload) => setRequest(payload);
    listeners.add(handler);
    return () => listeners.delete(handler);
  }, []);

  const handleClose = (result) => {
    if (request) request.resolve(result);
    setRequest(null);
  };

  return (
    <AlertDialog open={!!request} onOpenChange={(open) => { if (!open) handleClose(false); }}>
      <AlertDialogContent data-testid="global-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{request?.title}</AlertDialogTitle>
          {request?.message && (
            <AlertDialogDescription>{request.message}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => handleClose(false)} data-testid="global-confirm-cancel">
            {request?.cancelLabel || "Annuleren"}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => handleClose(true)}
            className={request?.destructive ? "bg-red-500 hover:bg-red-600" : undefined}
            data-testid="global-confirm-confirm"
          >
            {request?.confirmLabel || "Bevestigen"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
